import { combineReducers } from 'redux';
import { SubmissionError } from 'redux-form';
import { takeEvery, call, put, select } from 'redux-saga/effects';
import { replace } from 'react-router-redux';
import get from 'lodash.get';
import { showSnackbarAction } from './snackbar';
import { showAlert } from './alert';
import request from '../api-utils/request';
import actionKey from '../utils/action-key';
import takeOneAtMost from '../utils/takeOneAtMost';
import { REMOVE_POSITION, SAVE_POSITION } from './employer-settings';

const FETCH_SITES = 'sites/FETCH_SITES';
const FETCH_SITE = 'sites/FETCH_SITE';
const FETCH_COMPANY_FORM = 'sites/FETCH_COMPANY_FORM';
const CHANGE_QUERY = 'sites/CHANGE_QUERY';
const EDIT_COMPANY = 'sites/EDIT_COMPANY';
const ADD_COMPANY = 'sites/ADD_COMPANY';
const ADD_COMPANY_FORM = 'sites/ADD_COMPANY_FORM';
const SET_STEP = 'sites/REDUCE_STEP';
const UPLOAD_IMAGE = 'sites/UPLOAD_IMAGE';
const VERIFY_DOMAIN = 'sites/VERIFY_DOMAIN';
const SET_IMAGE = 'sites/SET_IMAGE';

export const companySizeRanges = {
    1: '1 - 10',
    2: '11 - 50',
    3: '51 - 200',
    4: '201 - 500',
    5: '501 - 1000',
    6: '1001 - 5000',
    7: '5001 - 10000',
    8: '10000+',
};

const sitesListReducer = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_SITE):
            return {
                ...state,
                [action.payload.companySetting.companyId]: action.payload.companySetting,
            };
        case actionKey.success(FETCH_SITES):
            return action.payload.companySettings.reduce((acc, company) => {
                acc[company.companyId] = company;
                return acc;
            }, {});
        case actionKey.success(ADD_COMPANY):
            return {
                ...state,
                [action.payload.companySettings.companyId]: action.payload.companySettings,
            };
        case actionKey.success(EDIT_COMPANY):
            return {
                ...state,
                [action.payload.companySettings.companyId]: action.payload.companySettings,
            };
        case actionKey.success(REMOVE_POSITION): {
            const positions = get(state, `${action.companyId}.positions`, []);
            return {
                ...state,
                [action.companyId]: {
                    ...state[action.companyId],
                    positions: positions.filter(position => (
                        Number(action.positionId) !== Number(position.companyPositionID_PK)
                    )),
                },
            };
        }
        case actionKey.success(SAVE_POSITION): {
            const companyId = action.payload.position.companyID_FK;
            let positions = get(state, `${companyId}.positions`, []);
            const position = action.payload.position;
            let found = false;
            positions = positions.map((existing) => {
                if (position.companyPositionID_PK === existing.companyPositionID_PK) {
                    found = true;
                    return position;
                }
                return existing;
            });
            if (!found) {
                positions.push(position);
            }
            positions = positions.sort((a, b) => (
                a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1
            ));
            return {
                ...state,
                [companyId]: {
                    ...state[companyId],
                    positions,
                },
            };
        }
        default:
            return state;
    }
};

const loadingReducer = (state = false, action) => {
    switch (action.type) {
        case actionKey.init(FETCH_SITES):
        case actionKey.init(FETCH_SITE):
        case actionKey.init(ADD_COMPANY_FORM):
        case actionKey.init(FETCH_COMPANY_FORM):
        case actionKey.init(VERIFY_DOMAIN):
            return true;
        case actionKey.failure(FETCH_SITES):
        case actionKey.failure(FETCH_SITE):
        case actionKey.failure(FETCH_COMPANY_FORM):
        case actionKey.failure(ADD_COMPANY_FORM):
        case actionKey.failure(VERIFY_DOMAIN):
        case actionKey.success(FETCH_SITES):
        case actionKey.success(FETCH_SITE):
        case actionKey.success(ADD_COMPANY_FORM):
        case actionKey.success(FETCH_COMPANY_FORM):
        case actionKey.success(VERIFY_DOMAIN):
            return false;
        default:
            return state;
    }
};

const isCompletedReducer = (state = false, action) => {
    switch (action.type) {
        case actionKey.init(ADD_COMPANY_FORM):
            return false;
        case actionKey.failure(ADD_COMPANY_FORM):
            return false;
        case actionKey.success(ADD_COMPANY_FORM):
            return true;
        default:
            return state;
    }
};

const errorReducer = (state = null, action) => {
    switch (action.type) {
        case FETCH_SITES:
        case FETCH_SITE:
        case actionKey.success(FETCH_SITES):
        case actionKey.success(FETCH_SITE):
            return null;
        case actionKey.failure(FETCH_SITES):
        case actionKey.failure(FETCH_SITE):
            return action.error;
        default:
            return state;
    }
};

const errorDomainReducer = (state = null, action) => {
    switch (action.type) {
        case actionKey.success(VERIFY_DOMAIN):
            return null;
        case actionKey.failure(VERIFY_DOMAIN):
            return action.error;
        default:
            return state;
    }
};

const searchReducer = (state = '', action) => {
    switch (action.type) {
        case CHANGE_QUERY:
            return action.value;
        default:
            return state;
    }
};

export const logoReducer = (state = null, action) => {
    switch (action.type) {
        case SET_IMAGE: {
            return action.img;
        }
        default:
            return state;
    }
};

export const logoLoadingReducer = (state = false, action) => {
    switch (action.type) {
        case UPLOAD_IMAGE:
            return true;
        case SET_IMAGE:
            return false;
        default:
            return state;
    }
};

export const companyFormReducer = (state = null, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_COMPANY_FORM):
            return { ...action.payload.formCompany };
        case actionKey.failure(FETCH_COMPANY_FORM):
            return state;
        default:
            return state;
    }
};

export const stepReducer = (state = 0, action) => {
    switch (action.type) {
        case SET_STEP: {
            return action.n;
        }
        case actionKey.failure(VERIFY_DOMAIN):
            return 1;
        case actionKey.success(VERIFY_DOMAIN):
            return 2;
        default:
            return state;
    }
};


export default combineReducers({
    logoLoading: logoLoadingReducer,
    sitesList: sitesListReducer,
    loading: loadingReducer,
    search: searchReducer,
    error: errorReducer,
    step: stepReducer,
    logo: logoReducer,
    companyForm: companyFormReducer,
    errorDomain: errorDomainReducer,
    isCompleted: isCompletedReducer,
});

export const fetchSitesAction = () => ({
    type: FETCH_SITES,
});

export const fetchSiteAction = id => ({
    type: FETCH_SITE,
    id,
});

export const setStepAction = n => ({
    type: SET_STEP,
    n,
});

export const fetchCompanyFormAction = token => ({
    type: FETCH_COMPANY_FORM,
    token,
});

export const changeQueryAction = value => ({
    type: CHANGE_QUERY,
    value,
});

export const updateCompanyAction = (body, companyId, resolve, reject) => ({
    type: EDIT_COMPANY,
    companyId,
    body,
    resolve,
    reject,
});

export const verifyDomainAction = (body, resolve, reject) => ({
    type: VERIFY_DOMAIN,
    body,
    resolve,
    reject,
});

export const addCompanyAction = (body, resolve, reject) => ({
    type: ADD_COMPANY,
    body,
    resolve,
    reject,
});

export const addCompanyClientAction = (body, id, resolve, reject) => ({
    type: ADD_COMPANY_FORM,
    id,
    body,
    resolve,
    reject,
});

export const uploadImageAction = () => ({
    type: UPLOAD_IMAGE,
});

export const setImageAction = img => ({
    type: SET_IMAGE,
    img,
});

export function* rootSaga() {
    yield [
        takeEvery(FETCH_SITES, fetchSites),
        takeEvery(FETCH_SITE, fetchSite),
        takeEvery(FETCH_COMPANY_FORM, fetchFormCompany),
        takeEvery(ADD_COMPANY, addCompany),
        takeEvery(ADD_COMPANY_FORM, addCompanyClient),
        takeEvery(VERIFY_DOMAIN, verifyDomain),
        takeEvery(VERIFY_DOMAIN, verifyDomain),
        takeOneAtMost(EDIT_COMPANY, editCompany),
        takeEvery([
            actionKey.failure(EDIT_COMPANY),
            actionKey.failure(ADD_COMPANY),
        ], showSnackbarError),
    ];
}

const getErrorDescription = (error) => {
    if (error === 'request entity too large') {
        return 'The logo is too large!';
    }
    return error;
};

export function* showSnackbarError(action) {
    yield put(showSnackbarAction({
        message: getErrorDescription(action.error),
        type: 'error',
        timeout: 5000,
    }));
}

function* verifyDomain({ type, body, resolve, reject }) {
    const result = yield call(request, {
        url: '/api/administration/verify-domain',
        type,
        options: {
            method: 'POST',
            body,
        },
    });
    if (result && result.error) {
        return yield reject(new SubmissionError({
            _error: result.error,
        }));
    }
    return yield resolve(result);
}

function* addCompany({ type, body, resolve, reject }) {
    const result = yield call(request, {
        url: '/api/administration/company-settings',
        type,
        options: {
            method: 'POST',
            body,
        },
    });

    if (result && result.error) {
        return yield reject(new SubmissionError({
            _error: result.error,
        }));
    }
    const companyId = get(result, 'payload.companySettings.companyId', null);
    let redirectTo = '/sites';
    if (companyId) {
        redirectTo = `/sites/${companyId}`;
    }
    yield put(replace(redirectTo));
    yield put(showAlert({
        title: 'Successfully added new Company',
        type: 'success',
        button: 'Close',
    }));
    return yield resolve(result);
}

function* addCompanyClient({ type, id, body, resolve, reject }) {
    const result = yield call(request, {
        url: '/api/administration/company-settings-add',
        type,
        options: {
            method: 'POST',
            body: {
                ...body,
                id,
            },
        },
    });
    if (result && result.error) {
        return yield reject(new SubmissionError({
            _error: result.error,
        }));
    }
    return yield resolve(result);
}

function* editCompany({ type, body, companyId, resolve, reject }) {
    const result = yield call(request, {
        url: `/api/administration/company-settings/${companyId}`,
        type,
        options: {
            method: 'PUT',
            body,
        },
    });

    if (result && result.error) {
        return yield reject(new SubmissionError({
            _error: result.error,
        }));
    }
    yield put(showAlert({
        title: 'Success!',
        type: 'success',
        message: 'Your changes have been saved successfully.',
        button: 'Close',
    }));
    return yield resolve(result);
}

function* fetchSites({ type }) {
    yield call(request, {
        url: '/api/administration/company-settings',
        type,
        options: {
            method: 'GET',
        },
    });
}

function* fetchSite({ type, id }) {
    const { sites } = yield select();
    if (sites.sitesList[id]) {
        return;
    }
    yield call(request, {
        url: `/api/administration/company-settings/${id}`,
        type,
        options: {
            method: 'GET',
        },
    });
}

function* fetchFormCompany({ type, token }) {
    yield call(request, {
        url: `/api/administration/company-form-check/${token}`,
        type,
        options: {
            method: 'GET',
        },
    });
}



// WEBPACK FOOTER //
// ./src/client/redux/sites.js